/**
 * LegalConsentGate (1.34.0)
 *
 * Гейт, который не пускает пользователя в приложение, пока тот не дал
 * согласие на актуальные версии всех юридических документов (privacy,
 * consent, terms).
 *
 * Где используется: в AuthBoundary, обёрткой над всеми защищёнными
 * маршрутами. Должен быть ПОСЛЕ загрузки user (то есть когда мы уже
 * знаем кто залогинен).
 *
 * Логика:
 *   1. Грузим все is_current документы из БД (3 строки).
 *   2. Грузим document_id из user_consents текущего юзера.
 *   3. Если для каждого документа есть согласие — пропускаем дальше.
 *   4. Если нет — показываем модалку с тремя ссылками + чекбоксом
 *      "Принимаю условия" + кнопками "Принять" / "Выйти".
 *   5. На "Принять" — записываем согласия и пропускаем. На "Выйти" —
 *      logout.
 *
 * Если загрузка падает (сеть, БД) — пропускаем гейт. Лучше пропустить
 * рабочий день, чем заблокировать на пустом месте. Если что-то сломано
 * с документами, админ увидит в логах ошибок и поправит.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { C, F } from '../design-system/tokens';
import { Button } from '../design-system/atoms/Button';
import { Spinner } from '../design-system/atoms/Spinner';
import { useAuthStore } from '../domains/auth/authStore';
import { fetchAllCurrentLegalDocuments, type LegalDocument } from '../domains/legal/api';
import { fetchUserConsentDocumentIds, recordUserConsents } from '../domains/legal/consents';

interface LegalConsentGateProps {
  children: ReactNode;
}

type GateState =
  | { phase: 'loading' }
  | { phase: 'allowed' }
  | { phase: 'needs_consent'; documents: LegalDocument[] }
  | { phase: 'error'; message: string };

export function LegalConsentGate({ children }: LegalConsentGateProps) {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const [state, setState] = useState<GateState>({ phase: 'loading' });
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) {
      // Без user гейт не имеет смысла — AuthBoundary сам перенаправит на /login
      setState({ phase: 'allowed' });
      return;
    }

    let cancelled = false;
    setState({ phase: 'loading' });
    setAccepted(false);

    void (async () => {
      try {
        const [docs, consentedIds] = await Promise.all([
          fetchAllCurrentLegalDocuments(),
          fetchUserConsentDocumentIds(user.id),
        ]);
        if (cancelled) return;

        // Если документов в БД нет (миграция не применена или БД пустая) —
        // нечего запрашивать, пропускаем гейт.
        if (docs.length === 0) {
          setState({ phase: 'allowed' });
          return;
        }

        const missing = docs.filter((d) => !consentedIds.has(d.id));
        if (missing.length === 0) {
          setState({ phase: 'allowed' });
        } else {
          setState({ phase: 'needs_consent', documents: missing });
        }
      } catch (e) {
        if (cancelled) return;
        console.error('[LegalConsentGate] check failed:', e);
        // На ошибке БД не блокируем приложение — пускаем
        setState({ phase: 'allowed' });
      }
    })();

    return () => { cancelled = true; };
  }, [user?.id]);

  const handleAccept = async () => {
    if (!user || state.phase !== 'needs_consent') return;
    setSubmitting(true);
    try {
      const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
      await recordUserConsents(
        user.id,
        state.documents.map((d) => d.id),
        ua,
      );
      setState({ phase: 'allowed' });
    } catch (e) {
      setState({
        phase: 'error',
        message: e instanceof Error ? e.message : 'Не удалось сохранить согласие',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    setSubmitting(true);
    try {
      await logout();
    } catch {
      // ignore — после logout мы всё равно уходим со страницы
    } finally {
      setSubmitting(false);
    }
  };

  if (state.phase === 'loading') {
    return (
      <div style={{
        height: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: C.bg,
      }}>
        <Spinner label="Проверяем согласие..." />
      </div>
    );
  }

  if (state.phase === 'error') {
    return (
      <div style={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: C.bg,
        padding: 20,
        textAlign: 'center',
        gap: 16,
      }}>
        <div style={{ fontSize: 14, color: C.re }}>Ошибка: {state.message}</div>
        <Button ch="Выйти" col="ghost" sz="md" onClick={() => void handleLogout()} />
      </div>
    );
  }

  if (state.phase === 'allowed') {
    return <>{children}</>;
  }

  // phase === 'needs_consent'
  return (
    <ConsentModal
      documents={state.documents}
      accepted={accepted}
      onAcceptedChange={setAccepted}
      onConfirm={() => void handleAccept()}
      onLogout={() => void handleLogout()}
      submitting={submitting}
    />
  );
}

// ── Модалка согласия ────────────────────────────────────────────────────

interface ConsentModalProps {
  documents: LegalDocument[];
  accepted: boolean;
  onAcceptedChange: (v: boolean) => void;
  onConfirm: () => void;
  onLogout: () => void;
  submitting: boolean;
}

function ConsentModal({
  documents,
  accepted,
  onAcceptedChange,
  onConfirm,
  onLogout,
  submitting,
}: ConsentModalProps) {
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.85)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 16,
      overflowY: 'auto',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 480,
        background: C.surf,
        border: `1px solid ${C.b}`,
        borderRadius: 16,
        padding: '24px 22px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}>
        <div>
          <div style={{
            fontFamily: F.head,
            fontWeight: 800,
            fontSize: 20,
            color: C.txt,
            marginBottom: 8,
          }}>
            Принятие условий
          </div>
          <div style={{ fontSize: 13, color: C.sub, lineHeight: 1.5 }}>
            Для продолжения работы в Кворум необходимо ознакомиться с
            документами ниже и подтвердить согласие.
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {documents.map((doc) => {
            const path = `/legal/${doc.kind}`;
            return (
              <Link
                key={doc.id}
                to={path}
                target="_blank"
                rel="noopener"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 14px',
                  background: C.card,
                  border: `1px solid ${C.b}`,
                  borderRadius: 10,
                  color: C.txt,
                  textDecoration: 'none',
                  fontSize: 14,
                  fontFamily: F.head,
                  fontWeight: 600,
                }}
              >
                <span>{doc.title}</span>
                <span style={{ fontSize: 12, color: C.acc }}>Открыть ↗</span>
              </Link>
            );
          })}
        </div>

        <label style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          cursor: 'pointer',
          padding: '12px 14px',
          background: accepted ? `${C.acc}11` : C.card,
          border: `1px solid ${accepted ? `${C.acc}55` : C.b}`,
          borderRadius: 10,
          transition: 'background .15s, border-color .15s',
        }}>
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => onAcceptedChange(e.target.checked)}
            disabled={submitting}
            style={{
              width: 18,
              height: 18,
              marginTop: 1,
              accentColor: C.acc,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          />
          <div style={{ fontSize: 13, color: C.txt, lineHeight: 1.5 }}>
            Я ознакомлен(а) с указанными документами и принимаю их условия.
          </div>
        </label>

        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <Button
            ch="Выйти"
            col="ghost"
            sz="md"
            onClick={onLogout}
            disabled={submitting}
          />
          <div style={{ flex: 1 }}>
            <Button
              fw
              ch="Принять и продолжить"
              col="acc"
              sz="md"
              onClick={onConfirm}
              disabled={!accepted || submitting}
              loading={submitting}
            />
          </div>
        </div>

        <div style={{
          fontSize: 11,
          color: C.sub,
          lineHeight: 1.5,
          textAlign: 'center',
          marginTop: 4,
        }}>
          Без принятия условий работа с приложением невозможна.
        </div>
      </div>
    </div>
  );
}
